import { useTranslate } from '@tolgee/react';
import { useSWRInfinite } from 'swr';
import { RefereeListItemFragment } from '@amf/shared/types/graphql';
import { useCallback, useMemo } from 'react';
import Button from '@amf/shared/components/button/Button';
import { RefereeLicense } from '@amf/shared/data/Referee';

const PATH = '/api/referee/list';
export const LIMIT = 20;

const getKey =
  (license?: RefereeLicense) =>
  (pageIndex: number, prevPageData: RefereeListItemFragment[] | null) => {
    const params = new URLSearchParams({ offset: `${pageIndex}` });
    if (license) params.append('license', license);

    if (prevPageData === null || pageIndex === 0) {
      return `${PATH}?${params.toString()}`;
    }

    if (prevPageData.length < LIMIT) {
      return null;
    }

    return `${PATH}/?${params.toString()}`;
  };

function useRefereeList(license: RefereeLicense | null = null) {
  const { t } = useTranslate();
  const { data, size, setSize, error } = useSWRInfinite<RefereeListItemFragment[]>(
    getKey(license ?? undefined),
  );

  const referees = useMemo<RefereeListItemFragment[]>(
    () => (data ? ([] as RefereeListItemFragment[]).concat(...data) : []),
    [data],
  );

  const loadMore = useCallback(() => {
    if (!referees || data === undefined) {
      return null;
    }

    if (size > data.length) {
      return (
        <Button type='button' variant='dark-outline' disabled>
          {t('match.loading')}
        </Button>
      );
    }

    if (LIMIT * size > referees.length) {
      return null;
    }

    return (
      <Button type='button' variant='dark-outline' onClick={() => setSize(size + 1)}>
        {t('match.more')}
      </Button>
    );
  }, [data, referees, setSize, size, t]);

  return {
    referees,
    loading: !data && !error,
    loadMore,
    setSize,
    size,
    loadingMore: data !== undefined && size > data.length,
  };
}

export default useRefereeList;
